import React from 'react';
import styles from './CharacterProfile.module.css';
import modalStyles from '../utils/EditProfileModal.module.css';
import EditProfileModal from '../utils/EditProfileModal';
import Notification from '../create/Notification';

interface ModalConfig {
  title: {
    edit: string;
    view: string;
  };
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  defaultValue?: string;
}

interface ModalState {
  type: string | null;
  mode: 'edit' | 'view';
  value: string;
  loading: boolean;
  error: string | null;
  config?: ModalConfig;
}

interface EditableFieldState {
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  value: string;
  setValue: (value: string) => void;
  isLoading: boolean;
  error: string | null;
  handleSubmit: (e?: React.FormEvent) => void;
}

interface NotificationType {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface CharacterProfileModalsProps {
  modalState: ModalState;
  closeModal: () => void;
  handleModalSubmit: (e: React.FormEvent) => void;
  handleModalValueChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  displayName: EditableFieldState;
  tagline: EditableFieldState;
  notification: NotificationType | null;
  setNotification: (notification: NotificationType | null) => void;
}

const CharacterProfileModals: React.FC<CharacterProfileModalsProps> = ({
  modalState,
  closeModal,
  handleModalSubmit,
  handleModalValueChange,
  displayName,
  tagline,
  notification,
  setNotification
}) => {
  return (
    <>
      <EditProfileModal
        title={modalState.config?.title[modalState.mode] || ''}
        isOpen={!!modalState.type}
        onClose={closeModal}
        onSubmit={modalState.mode === 'edit' ? handleModalSubmit : undefined}
        isLoading={modalState.loading}
        error={modalState.error || undefined}
      >
        {modalState.mode === 'edit' ? (
          <div className={modalStyles.modalEditorWrapper}>
            <textarea
              value={modalState.value}
              onChange={handleModalValueChange}
              placeholder={modalState.config?.placeholder}
              className={modalStyles.textArea}
              disabled={modalState.loading}
              rows={modalState.config?.rows}
              maxLength={modalState.config?.maxLength}
            />
            {modalState.config?.maxLength && (
              <span className={modalStyles.charCount}>
                {modalState.value.length}/{modalState.config.maxLength}
              </span>
            )}
          </div>
        ) : (
          <div className={styles.aboutValue}>
            {modalState.value || modalState.config?.defaultValue}
          </div>
        )}
      </EditProfileModal>

      <EditProfileModal
        title="Edit Display Name"
        isOpen={displayName.isEditing}
        onClose={() => displayName.setIsEditing(false)}
        onSubmit={displayName.handleSubmit}
        isLoading={displayName.isLoading}
        error={displayName.error || undefined}
      >
        <div className={modalStyles.modalEditorWrapper}>
          <input
            type="text"
            value={displayName.value}
            onChange={(e) => {
              // Limit to 20 characters (display name limit from CreateCharacter)
              if (e.target.value.length <= 20) {
                displayName.setValue(e.target.value);
              }
            }}
            placeholder="Enter new display name"
            className={modalStyles.nameInput}
            disabled={displayName.isLoading}
            maxLength={20}
          />
          <span className={modalStyles.charCount}>
            {displayName.value.length}/20
          </span>
        </div>
      </EditProfileModal>

      <EditProfileModal
        title="Edit Tagline"
        isOpen={tagline.isEditing}
        onClose={() => tagline.setIsEditing(false)}
        onSubmit={tagline.handleSubmit}
        isLoading={tagline.isLoading}
        error={tagline.error || undefined}
      >
        <div className={modalStyles.modalEditorWrapper}>
          <input
            type="text"
            value={tagline.value}
            onChange={(e) => {
              // Limit to 50 characters (tagline limit from CreateCharacter)
              if (e.target.value.length <= 50) {
                tagline.setValue(e.target.value);
              }
            }}
            placeholder="Enter new tagline"
            className={modalStyles.nameInput}
            disabled={tagline.isLoading}
            maxLength={50}
          />
          <span className={modalStyles.charCount}>
            {tagline.value.length}/50
          </span>
        </div>
      </EditProfileModal>

      {notification && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
          onClose={() => setNotification(null)} 
        />
      )}
    </>
  );
};

export default CharacterProfileModals; 